import { $createListNode, $isListNode } from '@lexical/list';
import {
    $createHeadingNode,
    $createQuoteNode,
    $isHeadingNode,
    $isQuoteNode,
} from '@lexical/rich-text';
import { $dfs } from '@lexical/utils';
import type { ElementNode, LexicalEditor } from 'lexical';
import { $createParagraphNode, $createTextNode, $getRoot, $isTextNode } from 'lexical';

import type { SerializedArticleNode } from 'js/src/client';

/**
 * Utilities to work with article data used by the frontend and backend
 * - Backend: generate_serializable_script_structure() output ("Article/Document" Script format)
 * - Frontend: Lexical editor state
 */

export function $convertLexicalStateToArticle(): Array<SerializedArticleNode> {
    const serializedArticle: Array<SerializedArticleNode> = [];

    let textIndex = 0;
    $dfs()
        .map(({ node }) => node)
        .filter($isTextNode)
        .filter((node) => node.getTextContent().trim())
        .forEach((node) => {
            let text = node.getTextContent();
            let blockType = 'text';
            const parentNode = node.getParent();
            if ($isHeadingNode(parentNode)) {
                if (parentNode.getTag() === 'h3') {
                    blockType = 'title';
                    text = node.getTextContent();
                } else if (parentNode.getTag() === 'h5') {
                    blockType = 'header_5';
                    text = node.getTextContent();
                }
            } else if ($isQuoteNode(parentNode)) {
                blockType = 'blockquote';
                text = node.getTextContent();
            } else if ($isListNode(parentNode)) {
                if (parentNode.getTag() === 'ol') {
                    blockType = 'ordered_list';
                    text = node.getTextContent();
                } else {
                    blockType = 'unordered_list';
                    text = node.getTextContent();
                }
            }
            serializedArticle.push({
                type: blockType,
                value: [
                    {
                        text,
                        index: textIndex,
                        url: '',
                    },
                ],
            });
            textIndex++;
        });
    return serializedArticle;
}

export const populateEditorWithArticleData = (
    editor: LexicalEditor,
    articleData: Array<SerializedArticleNode>,
) => {
    editor.update(() => {
        const $addBlockTextToNode = (parentNode: ElementNode, text: string) => {
            const nodeContainer = $createParagraphNode();
            nodeContainer.append($createTextNode(text));
            parentNode.append(nodeContainer);
        };
        const root = $getRoot();
        root.clear();
        const paragraphNode = $createParagraphNode();
        let lastType = '';
        let orderedListNumber = 1; // To keep track of the number for ordered lists.
        articleData.forEach(({ type, value }) => {
            if (type === 'ordered_list' && lastType === 'ordered_list') {
                orderedListNumber += 1;
            } else {
                orderedListNumber = 1;
            }
            if (type === 'title') {
                const wrapperNode = $createParagraphNode();
                const titleNode = $createHeadingNode('h4');
                value.forEach(({ text }) => {
                    titleNode.append($createTextNode(text));
                });
                wrapperNode.append(titleNode);
                paragraphNode.append(wrapperNode);
            } else if (type.startsWith('header')) {
                const wrapperNode = $createParagraphNode();
                const headerNode = $createHeadingNode('h5');
                value.forEach(({ text }) => {
                    headerNode.append($createTextNode(text));
                });
                wrapperNode.append(headerNode);
                paragraphNode.append(wrapperNode);
            } else
                switch (type) {
                    case 'blockquote': {
                        const quoteNode = $createQuoteNode();
                        value.forEach(({ text }) => {
                            if (text === undefined) return;
                            $addBlockTextToNode(quoteNode, text);
                        });
                        paragraphNode.append(quoteNode);

                        break;
                    }
                    case 'unordered_list': {
                        const listNode = $createListNode('bullet');
                        const listBlockNode = $createParagraphNode();
                        value.forEach(({ text }) => {
                            if (text === undefined) return;
                            $addBlockTextToNode(listBlockNode, text);
                        });
                        listNode.append(listBlockNode);
                        paragraphNode.append(listNode);

                        break;
                    }
                    case 'ordered_list': {
                        const listNode = $createListNode('number', orderedListNumber);
                        const listBlockNode = $createParagraphNode();
                        value.forEach(({ text }) => {
                            if (text === undefined) return;
                            $addBlockTextToNode(listBlockNode, text);
                        });
                        listNode.append(listBlockNode);
                        paragraphNode.append(listNode);

                        break;
                    }
                    case 'text': {
                        const textBlockNode = $createParagraphNode();
                        value.forEach(({ text }) => {
                            if (text === undefined) return;
                            $addBlockTextToNode(textBlockNode, text);
                        });
                        paragraphNode.append(textBlockNode);

                        break;
                    }
                    // No default
                }
            lastType = type;
        });
        root.append(paragraphNode);
    });
};
